import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowDown, ArrowUp, ChevronsUpDown,
  Eye, Pencil, Plus, Search, Trash2, Users,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/ui/pagination";
import { EmptyState } from "@/components/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";
import {
  estatusClienteEnum,
  tipoClienteEnum,
  type ClienteSummary,
  type EstadoDerivado,
  type EstatusCliente,
  type TipoCliente,
} from "./schemas";
import {
  useListClientes, useCliente,
  type ClienteSortBy, type SortDir,
} from "./api";
import { ClienteFormDialog } from "./ClienteFormDialog";
// v1.5.0-rc2: usar el modal cascada en vez del confirm() + DELETE simple.
import { DarDeBajaDialog } from "./DarDeBajaDialog";
// rc20+: chip de sub-empresa (auto-oculto si solo hay 1 empresa)
import { EmpresaChip } from "@/features/empresas/EmpresaChip";
// rc138 — móvil: tarjetas en vez de tabla
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

const TIPO_VARIANT: Record<TipoCliente, "default" | "secondary" | "outline"> = {
  residencial: "default",
  empresarial: "secondary",
  dedicado: "outline",
};
const ESTATUS_VARIANT: Record<
  EstatusCliente,
  "success" | "warning" | "destructive" | "secondary"
> = {
  activo: "success",
  suspendido: "warning",
  cancelado: "destructive",
  prospecto: "secondary",
};

// v1.1.5: Variant pa' el estado derivado (computed desde contratos en backend).
// Incluye "error" y "sin_servicio" que no existen en EstatusCliente manual.
const ESTADO_DERIVADO_VARIANT: Record<
  EstadoDerivado,
  "success" | "warning" | "destructive" | "secondary"
> = {
  activo: "success",
  error: "destructive",
  suspendido: "warning",
  cancelado: "destructive",
  sin_servicio: "secondary",
};

/**
 * Decide qué estado mostrar en el listado:
 *   - Si el backend devolvió `estado_derivado` y NO es "sin_servicio"
 *     (i.e. el cliente tiene contratos), muestra el derivado — refleja la
 *     realidad operativa (un cliente "activo" con contratos suspendidos
 *     debería verse como "suspendido").
 *   - Si no, muestra el `estatus` manual del cliente — útil pa' "prospecto"
 *     (sin contratos aún) o instalaciones legacy.
 */
function pickEstadoDisplay(c: ClienteSummary): {
  label: string;
  variant: "success" | "warning" | "destructive" | "secondary";
} {
  if (c.estado_derivado && c.estado_derivado !== "sin_servicio") {
    return {
      label: c.estado_derivado,
      variant: ESTADO_DERIVADO_VARIANT[c.estado_derivado],
    };
  }
  return { label: c.estatus, variant: ESTATUS_VARIANT[c.estatus] };
}

export function ClientesListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [tipo, setTipo] = useState<TipoCliente | "">("");
  const [estatus, setEstatus] = useState<EstatusCliente | "">("");
  // dev123: sort de columnas — default código asc
  const [sortBy, setSortBy] = useState<ClienteSortBy>("codigo");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false);
  // v1.5.0-rc5: auto-open del dialog cuando se llega con ?new=1 (atajo
  // desde las acciones rápidas del dashboard). Limpia el param de la URL
  // después para que el back-button funcione bien.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setCreateOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: editing } = useCliente(editingId);

  const filters = useMemo(
    () => ({ page, size: 20, search, tipo, estatus, sort_by: sortBy, sort_dir: sortDir }),
    [page, search, tipo, estatus, sortBy, sortDir]
  );

  // dev123: click en header → toggle dir si misma columna; cambia col + reset asc
  function toggleSort(col: ClienteSortBy) {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
    setPage(1);
  }

  const { data, isLoading, isFetching } = useListClientes(filters);
  const isMobile = useIsMobile();

  const totalPages = data?.pages ?? 1;
  const items = data?.items ?? [];

  // v1.5.0-rc2: target del modal de baja cascada (en vez de confirm() simple).
  // null = modal cerrado; cliente = modal abierto con preview de impacto.
  const [bajaTarget, setBajaTarget] = useState<
    { id: string; codigo: string; razon_social: string } | null
  >(null);

  return (
    <div className="flex flex-col gap-4">
      {/* Header + Crear */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Clientes</h2>
          <p className="text-xs text-muted-foreground">
            {data ? `${data.total} ${data.total === 1 ? "cliente" : "clientes"}` : "Cargando…"}
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> <span className="max-md:hidden">Nuevo cliente</span><span className="md:hidden">Nuevo</span>
        </Button>
      </div>

      {/* Filtros — rc138 móvil: búsqueda a lo ancho, selects a la mitad */}
      <div className="flex flex-wrap items-end gap-2 max-md:[&>div:not(:first-child)]:flex-1 max-md:[&>div:not(:first-child)]:min-w-0 max-md:[&_select]:w-full">
        <div className="relative flex-1 min-w-[220px] max-md:basis-full">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar por código, razón social o RFC…"
            className="pl-8"
          />
        </div>
        <div>
          <Select
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value as TipoCliente | "");
              setPage(1);
            }}
          >
            <option value="">Todos los tipos</option>
            {tipoClienteEnum.map((t) => (
              <option key={t} value={t}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Select
            value={estatus}
            onChange={(e) => {
              setEstatus(e.target.value as EstatusCliente | "");
              setPage(1);
            }}
          >
            <option value="">Todos los estatus</option>
            {estatusClienteEnum.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* rc138 — móvil: tarjetas (orden = el mismo sort del servidor) */}
      {isMobile ? (
        <div className="flex flex-col gap-3">
          {isLoading && items.length === 0 ? (
            <MobileEmpty>Cargando clientes…</MobileEmpty>
          ) : items.length === 0 ? (
            <MobileEmpty>{search || tipo || estatus ? "No hay coincidencias con los filtros." : "Crea el primer cliente para comenzar."}</MobileEmpty>
          ) : (
            <MobileList>
              {items.map((c) => {
                const e = pickEstadoDisplay(c);
                return (
                  <MobileCard
                    key={c.id}
                    to={`/clientes/${c.id}`}
                    title={<>{c.razon_social} <EmpresaChip empresaId={c.empresa_id} /></>}
                    subtitle={<><span className="font-mono">{c.codigo}</span>{c.nombre_comercial ? ` · ${c.nombre_comercial}` : ""}</>}
                    right={<Badge variant={e.variant}>{e.label}</Badge>}
                    actions={
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setEditingId(c.id)} disabled={c.estatus === "cancelado"}>
                          <Pencil size={13} /> Editar
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setBajaTarget({ id: c.id, codigo: c.codigo, razon_social: c.razon_social })} disabled={c.estatus === "cancelado"}>
                          <Trash2 size={13} /> Baja
                        </Button>
                      </>
                    }
                  >
                    <MobileMeta label="RFC" mono>{c.rfc}</MobileMeta>
                    <MobileMeta label="Tipo"><Badge variant={TIPO_VARIANT[c.tipo]}>{c.tipo}</Badge></MobileMeta>
                    <MobileMeta label="Alta">{formatDate(c.fecha_alta)}</MobileMeta>
                  </MobileCard>
                );
              })}
            </MobileList>
          )}
          <div className="rounded-lg border bg-card">
            <Pagination page={page} totalPages={totalPages} total={data?.total} size={data?.size} onPageChange={setPage} isFetching={isFetching} />
          </div>
        </div>
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader col="codigo" label="Código" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortableHeader col="razon_social" label="Razón social" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortableHeader col="rfc" label="RFC" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortableHeader col="tipo" label="Tipo" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortableHeader col="estatus" label="Estatus" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortableHeader col="fecha_alta" label="Alta" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <TableHead className="text-right pr-4">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando clientes…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={Users}
                  title="Sin clientes"
                  description={
                    search || tipo || estatus
                      ? "No hay coincidencias con los filtros."
                      : "Crea el primer cliente para comenzar."
                  }
                  action={
                    !search && !tipo && !estatus ? (
                      <Button size="sm" onClick={() => setCreateOpen(true)}>
                        <Plus size={14} /> Nuevo cliente
                      </Button>
                    ) : undefined
                  }
                />
              </TableEmpty>
            ) : (
              items.map((c) => (
                <TableRow
                  key={c.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/clientes/${c.id}`)}
                >
                  <TableCell className="font-mono">{c.codigo}</TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{c.razon_social}</span>
                      <EmpresaChip empresaId={c.empresa_id} />
                    </div>
                    {c.nombre_comercial && (
                      <div className="text-[10px] text-muted-foreground">{c.nombre_comercial}</div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono">{c.rfc}</TableCell>
                  <TableCell>
                    <Badge variant={TIPO_VARIANT[c.tipo]}>{c.tipo}</Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const e = pickEstadoDisplay(c);
                      return <Badge variant={e.variant}>{e.label}</Badge>;
                    })()}
                  </TableCell>
                  <TableCell>{formatDate(c.fecha_alta)}</TableCell>
                  <TableCell className="text-right">
                    <div
                      className="flex items-center justify-end gap-0.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/clientes/${c.id}`)}
                        title="Ver detalle"
                      >
                        <Eye size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingId(c.id)}
                        disabled={c.estatus === "cancelado"}
                        title="Editar"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setBajaTarget({
                            id: c.id,
                            codigo: c.codigo,
                            razon_social: c.razon_social,
                          })
                        }
                        disabled={c.estatus === "cancelado"}
                        title="Dar de baja al cliente con flujo cascada"
                      >
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Paginación — compacta con números y elipsis (v1.5.0-rc2) */}
        <Pagination
          page={page}
          totalPages={totalPages}
          total={data?.total}
          size={data?.size}
          onPageChange={setPage}
          isFetching={isFetching}
        />
      </div>
      )}

      {/* Crear */}
      <ClienteFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        cliente={null}
        onSaved={(id) => {
          setCreateOpen(false);
          navigate(`/clientes/${id}`);
        }}
      />
      {/* Editar rápido */}
      <ClienteFormDialog
        open={!!editingId && !!editing}
        onOpenChange={(v) => {
          if (!v) setEditingId(null);
        }}
        cliente={editing ?? null}
      />

      {/* v1.5.0-rc2: Modal cascada de baja (preview + motivo obligatorio +
          confirmación). Reemplaza al confirm() simple anterior. */}
      {bajaTarget && (
        <DarDeBajaDialog
          clienteId={bajaTarget.id}
          clienteNombre={`${bajaTarget.codigo} — ${bajaTarget.razon_social}`}
          onClose={() => setBajaTarget(null)}
          onSuccess={() => setBajaTarget(null)}
        />
      )}
    </div>
  );
}

// dev123: header de columna sortable. Click toggles asc/desc en la misma
// columna; cambiar de columna resetea a asc.
function SortableHeader({
  col, label, sortBy, sortDir, onClick,
}: {
  col: ClienteSortBy;
  label: string;
  sortBy: ClienteSortBy;
  sortDir: SortDir;
  onClick: (col: ClienteSortBy) => void;
}) {
  const active = sortBy === col;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onClick(col)}
        className={cn(
          "flex items-center gap-1 -ml-1 px-1 py-0.5 rounded hover:bg-muted/60 transition-colors",
          active && "text-foreground font-semibold",
        )}
        aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      >
        <span>{label}</span>
        {active ? (
          sortDir === "asc"
            ? <ArrowUp size={11} className="text-primary" />
            : <ArrowDown size={11} className="text-primary" />
        ) : (
          <ChevronsUpDown size={11} className="text-muted-foreground/50" />
        )}
      </button>
    </TableHead>
  );
}
